import type { GenericFormType } from '@neo/blocks/forms/types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'router-v6'
import { getAppDetails } from 'v2/pages/flow/utils'
import {
  useExternalAppsV2Query,
  useExternalOrgAppsQuery,
} from 'v2/pages/settings/integrations/queries'
import { getAccountIdentifier } from 'v2/pages/settings/integrations/utils/account-identifier'
import type { RExternalAppDetails } from 'v2/types/api/settings'
import { KBSources } from '../../../config'
import {
  useFetchCollectionArticles,
  useFetchDocumentsFromSource,
  useFetchDocumentsFromSourceByFetchId,
  useGetKnowledgeBase,
} from '../../../queries'
import type { McpArticle, McpArticleCollection } from '../../../types'
import { type KBAppDocument, KBDocumentSource, type NewKBFormValues } from '../../../types'
import { mergeCollectionItems } from './merge-collection-items'

export interface AppSourceOption {
  id: string // Unique identifier for each app instance
  provider: string
  name: string
  logoUrl?: string
  accountIdentifier?: string
  isConnected: boolean
}

export function useAppSourceData(form: GenericFormType<NewKBFormValues>) {
  const { data: availableApps } = useExternalAppsV2Query()
  const { data: connectedOrgApps } = useExternalOrgAppsQuery()

  const params = useParams()
  const kbId = params.id

  // Fetch knowledge base data if in edit mode
  const { data: knowledgeBase } = useGetKnowledgeBase(kbId || '')

  const selectedAppProvider = form.useStore((state) => state.values.app_source || '')
  const selectedAppSourceId = form.useStore((state) => state.values.app_source_id || '')
  const urlSelectionType = form.useStore((state) => state.values.url_selection_type || 'all')

  // Add refs to prevent infinite loops
  const previousAppDataRef = useRef<KBAppDocument[]>([])
  const isInitialLoadRef = useRef(true)

  const allAvailableApps: RExternalAppDetails[] = useMemo(() => {
    if (!availableApps) return []
    return Object.values(availableApps).flat()
  }, [availableApps])

  // Get all apps that support knowledge base (have MCP tools) along with their details
  const availableKnowledgeBaseSources: AppSourceOption[] = useMemo(() => {
    // Show all connected instances of each KBSources app, plus one entry if no connections exist
    const appOptions: AppSourceOption[] = []

    KBSources.forEach((source) => {
      const appDetails = allAvailableApps.find((app) => app.provider === source)
      const connectedApps =
        connectedOrgApps?.filter((app) => app.provider === source && app.state === 'ACTIVE') || []

      if (connectedApps.length > 0) {
        // Add one entry for each connected instance
        connectedApps.forEach((connectedApp) => {
          appOptions.push({
            id: connectedApp.id, // Use the connected app's unique ID
            provider: source,
            name: appDetails?.name || source,
            logoUrl: appDetails?.logo_url,
            accountIdentifier: getAccountIdentifier(connectedApp),
            isConnected: true,
          })
        })
      } else {
        // Add one entry for the provider even if not connected
        appOptions.push({
          id: source, // Use provider name as ID for unconnected apps
          provider: source,
          name: appDetails?.name || source,
          logoUrl: appDetails?.logo_url,
          accountIdentifier: undefined,
          isConnected: false,
        })
      }
    })

    return appOptions.sort((a, b) => {
      // Sort connected apps first, then by name
      if (a.isConnected && !b.isConnected) return -1
      if (!a.isConnected && b.isConnected) return 1
      return a.name.localeCompare(b.name)
    })
  }, [allAvailableApps, connectedOrgApps])

  // For backward compatibility, keep availableSources as provider strings
  const availableSources = useMemo(
    () => availableKnowledgeBaseSources.map((app) => app.provider),
    [availableKnowledgeBaseSources],
  )

  const isConnected = useCallback(
    (provider: string) => {
      if (!connectedOrgApps) return false
      return connectedOrgApps.some((app) => app.provider === provider && app.state === 'ACTIVE')
    },
    [connectedOrgApps],
  )

  const getAppLogo = useCallback(
    (appProvider: string) => getAppDetails(appProvider, availableApps)?.logo_url || undefined,
    [availableApps],
  )

  const getAppSourceOption = useCallback(
    (provider: string): AppSourceOption | undefined =>
      availableKnowledgeBaseSources.find((app) => app.provider === provider),
    [availableKnowledgeBaseSources],
  )

  const getAppSourceOptionById = useCallback(
    (id: string): AppSourceOption | undefined =>
      availableKnowledgeBaseSources.find((app) => app.id === id),
    [availableKnowledgeBaseSources],
  )

  // Fetch documents when app is selected and connected, but only if url_selection_type is 'selected'
  const isAppConnected = selectedAppProvider ? isConnected(selectedAppProvider) : false
  const shouldFetchDocuments = isAppConnected && urlSelectionType === 'selected'

  // Step 1: Initiate fetch to get fetch_id
  const { data: fetchResponse, isFetching: isInitiatingFetch } = useFetchDocumentsFromSource(
    shouldFetchDocuments ? selectedAppProvider : '',
    shouldFetchDocuments ? selectedAppSourceId : '',
  )

  // Step 2: Use fetch_id to get documents when available
  const fetchId = fetchResponse?.data?.fetch_id
  const { data: documentsResponse } = useFetchDocumentsFromSourceByFetchId(fetchId || '')

  // Determine loading state and final data - use local loading state instead of form field
  const documentsStatus = documentsResponse?.data?.status
  const isAppLoading =
    shouldFetchDocuments &&
    (isInitiatingFetch ||
      (fetchId && documentsStatus !== 'completed' && documentsStatus !== 'errored'))
  // SPACES-ONLY base tree (the mint requests articles=false): top-level collections with empty items;
  // each collection's pages are fetched lazily on expand. This is what makes a 11k-page Confluence
  // render in seconds instead of pulling every page up front.
  const baseDocumentsData = useMemo(
    () => (documentsStatus === 'completed' ? documentsResponse?.data?.data : []),
    [documentsStatus, documentsResponse?.data?.data],
  )

  // Lazy expand: pages loaded per collection (id → articles) + the set currently in flight (spinner).
  const fetchCollectionArticles = useFetchCollectionArticles(
    selectedAppProvider,
    selectedAppSourceId,
  )
  const [loadedArticles, setLoadedArticles] = useState<Record<string, McpArticle[]>>({})
  const [loadingCollectionIds, setLoadingCollectionIds] = useState<Set<string>>(new Set())
  // Spaces whose page list was CAPPED by the API (more pages exist than were returned) — drives the
  // "showing first N — select the whole space for the rest" footer.
  const [collectionHasMore, setCollectionHasMore] = useState<Record<string, boolean>>({})
  // In-flight set held in a REF (read synchronously) so two expands fired in the same tick can't both
  // pass the dedup guard and double-fetch — the loadingCollectionIds STATE is one render behind and
  // can't be trusted for that race. The state mirror only drives the spinner.
  const inFlightRef = useRef<Set<string>>(new Set())
  // Bumped on every app/source switch. A fetch that was already awaiting when the user switched will
  // still resolve AFTER the reset effect ran — without this guard its setState writes would land on the
  // NEW source's state (orphan articles, or a wrong has_more if a space id collides across two accounts
  // of the same vendor). The fetch captures the generation at call time and discards its result if the
  // generation has moved on.
  const generationRef = useRef(0)

  // Switching the app/source clears what we've lazily loaded so spaces don't bleed across connections.
  useEffect(() => {
    generationRef.current += 1
    setLoadedArticles({})
    setLoadingCollectionIds(new Set())
    setCollectionHasMore({})
    inFlightRef.current = new Set()
  }, [selectedAppProvider, selectedAppSourceId])

  const loadCollectionArticles = useCallback(
    async (collectionId: string) => {
      // Dedup: skip if already loaded (key PRESENT — not truthy, since a fetch that returned 0 pages is
      // a valid loaded-empty), or a fetch is in flight (synchronous ref read, race-safe).
      if (collectionId in loadedArticles || inFlightRef.current.has(collectionId)) return
      const generation = generationRef.current
      inFlightRef.current.add(collectionId)
      setLoadingCollectionIds((prev) => new Set(prev).add(collectionId))
      try {
        const res = await fetchCollectionArticles(collectionId)
        // Discard if the user switched app/source while this was in flight (the reset already ran;
        // writing now would pollute the NEW source's state).
        if (generationRef.current !== generation) return
        setLoadedArticles((prev) => ({ ...prev, [collectionId]: res.data?.articles ?? [] }))
        setCollectionHasMore((prev) => ({ ...prev, [collectionId]: !!res.data?.hasMore }))
      } catch {
        // On failure write NOTHING to loadedArticles — leaving the key absent so collapsing +
        // re-expanding RETRIES (writing [] would mark it "loaded-empty" and block the retry forever).
      } finally {
        inFlightRef.current.delete(collectionId)
        setLoadingCollectionIds((prev) => {
          const next = new Set(prev)
          next.delete(collectionId)
          return next
        })
      }
    },
    [fetchCollectionArticles, loadedArticles],
  )

  // Merge lazily-loaded pages into the spaces-only tree so each expanded space shows its own pages.
  // Uses mergeCollectionItems so an EMPTY lazy result never clobbers already-loaded base children — see
  // that helper for the full "appear then vanish" rationale (applies to ALL non-lazy sources: Notion,
  // Freshdesk, Zendesk, Intercom — anything that ships the full tree upfront; Confluence is the only
  // lazy one and is unaffected).
  const finalDocumentsData = useMemo(() => {
    if (!Array.isArray(baseDocumentsData)) return []
    return baseDocumentsData.map((node) =>
      node.type === 'collection'
        ? ({
            ...node,
            items: mergeCollectionItems(node.items, loadedArticles[node.id]),
          } as McpArticleCollection)
        : node,
    )
  }, [baseDocumentsData, loadedArticles])

  // Get default checked items from knowledge base config
  const defaultCheckedIds = useMemo(() => {
    if (!knowledgeBase?.sources || !selectedAppProvider) return []

    const appSource = knowledgeBase.sources.find(
      (source) =>
        source.source_type === KBDocumentSource.APP &&
        source.config.app_source === selectedAppProvider &&
        source.config.app_source_id === selectedAppSourceId,
    )

    if (!appSource?.config.app_data) return []

    return appSource.config.app_data.map((item) => ('id' in item ? item.id : item.document_id))
  }, [knowledgeBase, selectedAppProvider, selectedAppSourceId])

  return {
    availableSources,
    availableKnowledgeBaseSources,
    selectedAppProvider,
    selectedAppSourceId,
    urlSelectionType,
    isConnected,
    getAppLogo,
    getAppSourceOption,
    getAppSourceOptionById,
    isAppLoading,
    finalDocumentsData,
    defaultCheckedIds,
    knowledgeBase,
    previousAppDataRef,
    isInitialLoadRef,
    loadCollectionArticles,
    loadingCollectionIds,
    collectionHasMore,
  }
}
